import axios from 'axios';
import { Customer } from '../types/types';
import api from './axiosBase';
import { handleAxiosError } from './errorHandler';
import { useUserStore } from '@/store/userStore';

import { API_BASE } from './apiBase';

export interface ListCustomersResponse {
  Users: Customer[];
  StartAfter: string;
}

export const listCustomers = async (
  ProjectID: string,
  pageSize: number,
  startAfterId?: string,
  sortField?: string,
  sortDirection?: 'asc' | 'desc',
  search?: string
): Promise<ListCustomersResponse | null> => {
  const params: {
    pageSize: number;
    startAfter?: string;
    sortField?: string;
    sortDirection?: string;
    search?: string;
  } = { pageSize };
  if (startAfterId) {
    params.startAfter = startAfterId;
  }
  if (sortField) {
    params.sortField = sortField;
  }
  if (sortDirection) {
    params.sortDirection = sortDirection;
  }
  if (search) {
    params.search = search;
  }

  const userStore = useUserStore();
  const idToken = userStore.idToken;

  try {
    const response = await api.get(`/v2/project/${ProjectID}/users`, {
      params,
      headers: {
        Authorization: `Bearer ${idToken}`
      }
    });
    return response.data;
  } catch (error) {
    handleAxiosError(error);
    return null;
  }
};

export const getSimpleID = async () => {
  const response = await axios.get(`${API_BASE}/api/v2/project`);
  return response.data.SimpleID;
};

export const searchCustomers = async (SimpleID: string, text: string) => {
  return axios.get(`${API_BASE}/api/v2/project/${SimpleID}/customers`, {
    params: { text }
  });
};

export const exportCustomers = async (SimpleID: string) => {
  return axios.get(`${API_BASE}/api/v2/project/${SimpleID}/customers/export`);
};

export const addCustomer = async (SimpleID: string, customer: Customer) => {
  return axios.post(
    `${API_BASE}/api/v2/project/${SimpleID}/customers`,
    customer
  );
};

export const sendEmail = async (
  SimpleID: string,
  customerId: string,
  subject: string,
  messageBody: string
) => {
  return axios.post(
    `${API_BASE}/api/v2/project/${SimpleID}/customers/${customerId}/communication/email`,
    {
      subject,
      messageBody
    }
  );
};

export const sendMessage = async (
  SimpleID: string,
  customerId: string,
  messageBody: string
) => {
  return axios.post(
    `${API_BASE}/api/v2/project/${SimpleID}/customers/${customerId}/communication/message`,
    {
      messageBody
    }
  );
};

export const editCustomer = async (
  projectID: string,
  simpleId: string,
  updatedCustomer: Partial<Customer>
) => {
  // Use the user route with SimpleID
  return api.put(`/v2/project/${projectID}/user/${simpleId}`, {
    name: updatedCustomer.name,
    email: updatedCustomer.email,
    phone: updatedCustomer.phone,
    fluid: updatedCustomer.fluid,
    emailConsent: updatedCustomer.emailConsent,
    smsConsent: updatedCustomer.smsConsent,
    marketingConsent: updatedCustomer.marketingConsent
  });
};

export const deleteCustomer = async (projectID: string, simpleId: string) => {
  const userStore = useUserStore();
  const idToken = userStore.idToken;

  return api.delete(`/v2/project/${projectID}/user/${simpleId}`, {
    headers: {
      Authorization: `Bearer ${idToken}`
    }
  });
};

export const deleteCustomers = async (
  projectID: string,
  simpleIds: string[]
) => {
  const userStore = useUserStore();
  const idToken = userStore.idToken;

  // Delete customers one by one
  const deletePromises = simpleIds.map((simpleId) =>
    api.delete(`/v2/project/${projectID}/user/${simpleId}`, {
      headers: {
        Authorization: `Bearer ${idToken}`
      }
    })
  );

  return Promise.all(deletePromises);
};

export const generateCustomerFluid = async (
  projectID: string,
  simpleId: string
) => {
  return api.post(`/v2/project/${projectID}/user/${simpleId}/generate-fluid`);
};
